import React, { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Mail, Send, CheckCircle, MessageSquare, Bug, Lightbulb, 
  CreditCard, Handshake, HelpCircle, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '../../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'general', label: 'Genel Soru', icon: HelpCircle, color: 'text-blue-400' },
  { value: 'bug', label: 'Hata Bildirimi', icon: Bug, color: 'text-red-400' },
  { value: 'feature', label: 'Özellik Talebi', icon: Lightbulb, color: 'text-amber-400' },
  { value: 'billing', label: 'Ödeme / Fatura', icon: CreditCard, color: 'text-green-400' },
  { value: 'partnership', label: 'İş Ortaklığı', icon: Handshake, color: 'text-purple-400' }
];

const IletisimPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    category: 'general'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/contact`, form);
      setTicketId(response.data.ticket_id);
      setSubmitted(true);
      toast.success('Mesajınız başarıyla gönderildi!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSubmitted(false);
    setTicketId('');
    setForm({
      name: '',
      email: '',
      subject: '',
      message: '',
      category: 'general'
    });
  };

  return (
    <>
      <SEO
        title="İletişim"
        description="Borsamex ile iletişime geçin. Sorularınız ve önerileriniz için bize ulaşın."
        keywords="borsamex iletişim, destek, contact"
        url="/kurumsal/iletisim"
      />
      
      <div className="min-h-[calc(100vh-80px)] bg-brand-dark py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold mb-2">Bize Ulaşın</h1>
            <p className="text-slate-400">Sorularınız için bize yazın, en kısa sürede dönüş yapalım.</p>
          </div>

          <div className="grid lg:grid-cols-5 gap-6">
            {/* Left: Contact Info */}
            <div className="lg:col-span-2 space-y-4">
              {/* Info Cards */}
              <Card className="border-slate-700/50">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="p-2.5 rounded-lg bg-primary/10 shrink-0">
                    <Mail className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">E-posta</h3>
                    <a href="mailto:info@borsamex.com" className="text-sm text-primary hover:underline">
                      info@borsamex.com
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-700/50">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="p-2.5 rounded-lg bg-green-500/10 shrink-0">
                    <Clock className="h-5 w-5 text-green-400" />
                  </div>
                  <div>
                    <h3 className="font-medium">Yanıt Süresi</h3>
                    <p className="text-sm text-slate-400">Genellikle 24 saat içinde</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-700/50">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="p-2.5 rounded-lg bg-amber-500/10 shrink-0">
                    <MessageSquare className="h-5 w-5 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-medium">SSS</h3>
                    <a href="/sss" className="text-sm text-primary hover:underline">SSS'ye git →</a>
                  </div>
                </CardContent>
              </Card>

              {/* Category Legend */}
              <Card className="border-slate-700/50">
                <CardContent className="p-4">
                  <h3 className="font-medium text-sm mb-3 text-slate-400">Kategoriler</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {CATEGORIES.map(cat => {
                      const Icon = cat.icon;
                      return (
                        <div key={cat.value} className="flex items-center gap-2 text-sm">
                          <Icon className={`h-4 w-4 ${cat.color}`} />
                          <span className="text-slate-400">{cat.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right: Contact Form */}
            <div className="lg:col-span-3">
              <Card className="border-slate-700/50 h-full">
                <CardContent className="p-5">
                  {submitted ? (
                    /* Success State */
                    <div className="text-center py-12">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                        <CheckCircle className="h-8 w-8 text-green-400" />
                      </div>
                      <h2 className="text-xl font-bold mb-2">Mesajınız Alındı!</h2>
                      <p className="text-slate-400 mb-4">
                        En kısa sürede size dönüş yapacağız.
                      </p>
                      <div className="inline-block px-4 py-2 bg-slate-800 rounded-lg mb-6">
                        <p className="text-xs text-slate-500 mb-1">Takip Numaranız</p>
                        <p className="text-lg font-mono font-bold text-primary">#{ticketId}</p>
                      </div>
                      <div>
                        <Button variant="outline" onClick={handleReset}>
                          Yeni Mesaj Gönder
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* Form */
                    <form onSubmit={handleSubmit} className="space-y-4">
                      {/* Name & Email */}
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm text-slate-300">Ad Soyad</Label>
                          <Input
                            value={form.name}
                            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="Adınız Soyadınız"
                            className="mt-1.5 h-10 bg-slate-800/50"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-slate-300">E-posta</Label>
                          <Input
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                            placeholder="ornek@mail.com"
                            className="mt-1.5 h-10 bg-slate-800/50"
                            required
                          />
                        </div>
                      </div>

                      {/* Category & Subject */}
                      <div className="grid sm:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-sm text-slate-300">Kategori</Label>
                          <Select
                            value={form.category}
                            onValueChange={(v) => setForm(prev => ({ ...prev, category: v }))}
                          >
                            <SelectTrigger className="mt-1.5 h-10 bg-slate-800/50">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CATEGORIES.map(cat => (
                                <SelectItem key={cat.value} value={cat.value}>
                                  {cat.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-sm text-slate-300">Konu</Label>
                          <Input
                            value={form.subject}
                            onChange={(e) => setForm(prev => ({ ...prev, subject: e.target.value }))}
                            placeholder="Kısa bir başlık"
                            className="mt-1.5 h-10 bg-slate-800/50"
                            required
                          />
                        </div>
                      </div>

                      {/* Message */}
                      <div>
                        <Label className="text-sm text-slate-300">Mesajınız</Label>
                        <Textarea
                          value={form.message}
                          onChange={(e) => setForm(prev => ({ ...prev, message: e.target.value }))}
                          placeholder="Detaylı açıklama yazın..."
                          className="mt-1.5 bg-slate-800/50 min-h-[120px]"
                          required
                        />
                      </div>

                      {/* Submit */}
                      <Button type="submit" className="w-full h-11" disabled={loading}>
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Gönderiliyor...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Send className="h-4 w-4" />
                            Mesajı Gönder
                          </span>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default IletisimPage;
